import { hubApiAuthHeader } from "@/lib/hubApiAuthHeader";
import { onHubCachedAccessTokenChange } from "@/lib/hubAuthAccessToken";
import { hubLiveStreamApiUrl } from "@/lib/hubApiOrigin";
import type { HubCacheSseEvent } from "@/lib/hubCacheChannels";
import { HUB_LIVE_STREAM_PATH, unionHubLiveChannels } from "@/lib/hubLiveChannelUnion";
import type { HubLiveSseEvent } from "@/lib/hubLiveEvents";
import { connectHubLiveOwner } from "@/lib/hubLiveOwner";
import type { HubLiveOwnerToWorker, HubLiveWorkerToOwner } from "@/lib/hubLiveProtocol";
import {
  signalHubLifecycleReconnected,
  signalHubServerDraining,
} from "@/lib/hubLifecycleEvents";
import {
  applyHubLivePoolEvent,
  markHubDbPoolHolderConnecting,
} from "@/lib/hubDbPoolHolderStreamState";
import {
  HUB_STREAM_CLEAN_CLOSE_RECONNECT_MS,
  hubStreamTransportReconnectMs,
} from "@/lib/hubStreamReconnectDelay";
import { isHubStreamAuthError, isHubStreamTerminalError } from "@/lib/hubStreamResponseError";
import {
  clearHubSessionLiveStreamOpen,
  notifyHubSessionLiveStreamOpen,
} from "@/lib/hubSessionLive";

type CacheListener = {
  id: string;
  channels: ReadonlySet<string>;
  onEvent: (event: HubCacheSseEvent) => void;
};

const cacheListeners = new Map<string, CacheListener>();
const remoteTabChannels = new Map<string, string[]>();
let sessionStarts = 0;
let sawLiveConnected = false;
let worker: SharedWorker | null = null;
let workerPort: MessagePort | null = null;
let usingFallback = false;
let fallbackStop: (() => void) | null = null;
let subscriptionSeq = 0;
let tokenUnsub: (() => void) | null = null;
let fallbackSubsChannel: BroadcastChannel | null = null;
const fallbackTabId = `tab-${Math.random().toString(36).slice(2)}`;

function nextSubscriptionId(): string {
  subscriptionSeq += 1;
  return `sub-${subscriptionSeq}`;
}

function authHeaderValue(): string {
  return hubApiAuthHeader().Authorization ?? "";
}

function streamUrl(): string {
  return hubLiveStreamApiUrl(HUB_LIVE_STREAM_PATH);
}

export function getHubLiveTransportDiagnostics(): Record<string, unknown> {
  return {
    streamUrl: streamUrl(),
    ownerMode: workerPort != null ? "shared-worker" : usingFallback ? "broadcast-fallback" : "stopped",
    connectedEventSeen: sawLiveConnected,
    localSubscriptionCount: cacheListeners.size,
    remoteTabCount: remoteTabChannels.size,
    sessionStarts,
  };
}

function postToWorker(message: HubLiveWorkerToOwner): void {
  workerPort?.postMessage(message);
}

function localChannelGroups(): string[][] {
  return [...cacheListeners.values()].map((listener) => [...listener.channels]);
}

function allChannelGroups(): string[][] {
  return [...localChannelGroups(), ...remoteTabChannels.values()];
}

function publishFallbackSubs(): void {
  fallbackSubsChannel?.postMessage({
    type: "subs",
    tabId: fallbackTabId,
    channels: unionHubLiveChannels(localChannelGroups()),
  });
}

function dispatchLiveEvent(event: HubLiveSseEvent): void {
  if (event.kind === "connected") {
    notifyHubSessionLiveStreamOpen();
    if (sawLiveConnected) {
      signalHubLifecycleReconnected();
    }
    sawLiveConnected = true;
    return;
  }
  if (event.kind === "lifecycle" && event.phase === "server_draining") {
    signalHubServerDraining();
    return;
  }
  if (event.kind === "pool") {
    applyHubLivePoolEvent(event);
    if (event.phase === "connected") {
      notifyHubSessionLiveStreamOpen();
    }
    return;
  }
  for (const listener of cacheListeners.values()) {
    if (!listener.channels.has(event.channel)) {
      continue;
    }
    const cacheEvent: HubCacheSseEvent = {
      phase: event.phase,
      channel: event.channel,
      cached_at: event.cached_at,
      stale: event.stale,
      revalidating: event.revalidating,
      next_refresh_after: event.next_refresh_after,
      labor_reason: event.labor_reason,
      labor_session_id: event.labor_session_id,
      labor_page: event.labor_page,
      labor_tab_id: event.labor_tab_id,
      labor_idle_timeout: event.labor_idle_timeout,
      page_presence_reason: event.page_presence_reason,
      page_presence_route_key: event.page_presence_route_key,
      page_presence_viewers: event.page_presence_viewers,
      unread_count: event.unread_count,
      production_calendar_batch_id: event.production_calendar_batch_id,
      production_calendar_batch: event.production_calendar_batch,
    };
    listener.onEvent(cacheEvent);
  }
}

function handleOwnerMessage(message: HubLiveOwnerToWorker): void {
  if (message.type === "event") {
    dispatchLiveEvent(message.event);
  }
}

function sendHello(): void {
  postToWorker({
    type: "hello",
    origin: window.location.origin,
    streamUrl: streamUrl(),
    authorization: authHeaderValue(),
  });
}

function startSharedWorker(): boolean {
  if (typeof SharedWorker === "undefined") {
    return false;
  }
  try {
    worker = new SharedWorker(new URL("../workers/hubLiveSharedWorker.ts", import.meta.url), {
      type: "module",
      name: "sgs-hub-live",
    });
    workerPort = worker.port;
    worker.addEventListener("error", () => {
      worker = null;
      workerPort = null;
      if (!usingFallback) {
        startFallbackOwner();
      }
    });
    workerPort.onmessage = (event: MessageEvent<HubLiveOwnerToWorker>) => {
      handleOwnerMessage(event.data);
    };
    workerPort.start();
    sendHello();
    return true;
  } catch {
    worker = null;
    workerPort = null;
    return false;
  }
}

function startFallbackOwner(): void {
  if (usingFallback) {
    return;
  }
  usingFallback = true;
  const events = new BroadcastChannel("sgs-hub-live");
  fallbackSubsChannel = new BroadcastChannel("sgs-hub-live-subs");
  events.onmessage = (event: MessageEvent<HubLiveOwnerToWorker>) => {
    handleOwnerMessage(event.data);
  };
  fallbackSubsChannel.onmessage = (event: MessageEvent<{ type?: string; tabId?: string; channels?: string[] }>) => {
    const payload = event.data;
    if (payload.type !== "subs" || typeof payload.tabId !== "string" || payload.tabId === fallbackTabId) {
      return;
    }
    remoteTabChannels.set(payload.tabId, Array.isArray(payload.channels) ? payload.channels : []);
  };
  publishFallbackSubs();

  let stopped = false;
  let ownerStop: (() => void) | null = null;
  const abort = new AbortController();

  const runOwner = (): void => {
    let reconnectTimer: number | undefined;
    let active: AbortController | null = null;
    let reconnectBlocked: "auth" | "terminal" | null = null;
    let consecutive = 0;
    let lastKey = "";

    const stopLoop = (): void => {
      if (reconnectTimer !== undefined) {
        window.clearTimeout(reconnectTimer);
      }
      active?.abort();
    };

    const schedule = (delayMs: number): void => {
      if (stopped || reconnectBlocked != null) {
        return;
      }
      reconnectTimer = window.setTimeout(() => {
        void loop();
      }, delayMs);
    };

    const loop = async (): Promise<void> => {
      const authorization = authHeaderValue();
      if (authorization === "") {
        reconnectBlocked = "auth";
        return;
      }
      if (reconnectBlocked === "auth") {
        reconnectBlocked = null;
      }
      const channels = unionHubLiveChannels(allChannelGroups());
      lastKey = channels.join("\u0000");
      active?.abort();
      active = new AbortController();
      const signal = active.signal;
      try {
        await connectHubLiveOwner({
          streamUrl: streamUrl(),
          authorization,
          channels,
          signal,
          onEvent: (liveEvent) => {
            consecutive = 0;
            const payload: HubLiveOwnerToWorker = { type: "event", event: liveEvent };
            events.postMessage(payload);
            handleOwnerMessage(payload);
          },
        });
        if (stopped || signal.aborted) {
          return;
        }
        consecutive = 0;
        schedule(HUB_STREAM_CLEAN_CLOSE_RECONNECT_MS);
      } catch (error) {
        if (stopped || signal.aborted) {
          return;
        }
        if (isHubStreamAuthError(error)) {
          reconnectBlocked = "auth";
          return;
        }
        if (isHubStreamTerminalError(error)) {
          reconnectBlocked = "terminal";
          return;
        }
        consecutive += 1;
        schedule(hubStreamTransportReconnectMs(consecutive));
      }
    };

    let channelDebounceTimer: number | undefined;
    const scheduleChannelRestart = (): void => {
      if (channelDebounceTimer !== undefined) {
        window.clearTimeout(channelDebounceTimer);
      }
      channelDebounceTimer = window.setTimeout(() => {
        channelDebounceTimer = undefined;
        if (stopped) {
          return;
        }
        const nextKey = unionHubLiveChannels(allChannelGroups()).join("\u0000");
        if (nextKey === lastKey) {
          return;
        }
        stopLoop();
        void loop();
      }, 400);
    };

    const channelWatch = window.setInterval(() => {
      if (stopped) {
        return;
      }
      const nextKey = unionHubLiveChannels(allChannelGroups()).join("\u0000");
      if (nextKey !== lastKey) {
        scheduleChannelRestart();
      }
    }, 250);

    ownerStop = () => {
      if (channelDebounceTimer !== undefined) {
        window.clearTimeout(channelDebounceTimer);
      }
      window.clearInterval(channelWatch);
      stopLoop();
    };
    void loop();
  };

  const lockManager = (
    navigator as Navigator & {
      locks?: { request: (name: string, callback: () => Promise<void>) => Promise<void> };
    }
  ).locks;
  if (lockManager == null) {
    runOwner();
  } else {
    void lockManager.request("sgs-hub-live-owner", async () => {
      if (stopped) {
        return;
      }
      runOwner();
      await new Promise<void>((resolve) => {
        abort.signal.addEventListener("abort", () => resolve(), { once: true });
      });
    });
  }

  fallbackStop = () => {
    stopped = true;
    abort.abort();
    ownerStop?.();
    events.close();
    fallbackSubsChannel?.close();
    fallbackSubsChannel = null;
    remoteTabChannels.clear();
  };
}

function ensureTransport(): void {
  if (workerPort != null || usingFallback) {
    return;
  }
  markHubDbPoolHolderConnecting();
  if (!startSharedWorker()) {
    startFallbackOwner();
  }
}

function stopTransportIfIdle(): void {
  if (sessionStarts > 0 || cacheListeners.size > 0) {
    return;
  }
  queueMicrotask(() => {
    if (sessionStarts > 0 || cacheListeners.size > 0) {
      return;
    }
    workerPort?.close();
    worker = null;
    workerPort = null;
    fallbackStop?.();
    fallbackStop = null;
    usingFallback = false;
    sawLiveConnected = false;
    clearHubSessionLiveStreamOpen();
  });
}

function ensureTokenWatch(): void {
  if (tokenUnsub != null) {
    return;
  }
  tokenUnsub = onHubCachedAccessTokenChange((token) => {
    if (token == null || token === "") {
      return;
    }
    if (workerPort != null) {
      sendHello();
    }
  });
}

/** Holds the browser-wide live socket (pool + lifecycle + cache). Refcounted. */
export function startHubLiveSession(): () => void {
  ensureTokenWatch();
  sessionStarts += 1;
  ensureTransport();
  return () => {
    sessionStarts = Math.max(0, sessionStarts - 1);
    stopTransportIfIdle();
  };
}

export function subscribeHubLiveCacheChannels(options: {
  channels: readonly string[];
  onEvent: (event: HubCacheSseEvent) => void;
}): () => void {
  ensureTokenWatch();
  const id = nextSubscriptionId();
  cacheListeners.set(id, {
    id,
    channels: new Set(options.channels),
    onEvent: options.onEvent,
  });
  ensureTransport();
  postToWorker({ type: "subscribe", subscriptionId: id, channels: [...options.channels] });
  publishFallbackSubs();
  return () => {
    cacheListeners.delete(id);
    postToWorker({ type: "unsubscribe", subscriptionId: id });
    publishFallbackSubs();
    stopTransportIfIdle();
  };
}

export function resetHubLiveBrokerForTests(): void {
  cacheListeners.clear();
  remoteTabChannels.clear();
  sessionStarts = 0;
  sawLiveConnected = false;
  workerPort?.close();
  worker = null;
  workerPort = null;
  fallbackStop?.();
  fallbackStop = null;
  usingFallback = false;
  tokenUnsub?.();
  tokenUnsub = null;
}
